import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "./TestimonialVideoModal.scss";

export type TestimonialVideoPayload = {
  name: string;
  role: string;
  googleDriveFileId?: string;
  src?: string;
  useSampleVideo?: boolean;
};

const SAMPLE_VIDEO_SRC =
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.webm";

function googleDrivePreviewUrl(fileId: string) {
  return `https://drive.google.com/file/d/${fileId}/preview?autoplay=1`;
}

function googleDriveStreamUrl(fileId: string) {
  return `https://drive.google.com/uc?export=download&id=${fileId}`;
}

function resolveVideoSrc(video: TestimonialVideoPayload): string | undefined {
  if (video.googleDriveFileId) return undefined;
  if (video.useSampleVideo) return SAMPLE_VIDEO_SRC;
  return video.src;
}

type TestimonialVideoModalProps = {
  show: boolean;
  onHide: () => void;
  video: TestimonialVideoPayload | null;
};

const TestimonialVideoModal = ({ show, onHide, video }: TestimonialVideoModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const driveId = video?.googleDriveFileId;
  const fileSrc = video ? resolveVideoSrc(video) : undefined;
  const [driveUseIframe, setDriveUseIframe] = useState(false);

  useEffect(() => {
    if (!show) {
      setDriveUseIframe(false);
    }
  }, [show, driveId]);

  const playInlineVideo = useCallback(async () => {
    const el = videoRef.current;
    if (!el) return;
    try {
      el.muted = false;
      await el.play();
    } catch {
      try {
        el.muted = true;
        await el.play();
      } catch {
        // browser blocked autoplay
      }
    }
  }, []);

  useEffect(() => {
    if (!show || driveUseIframe) return;
    void playInlineVideo();
  }, [show, fileSrc, driveId, driveUseIframe, playInlineVideo]);

  const handleEntered = () => {
    if (!driveUseIframe) {
      void playInlineVideo();
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      onEntered={handleEntered}
      centered
      size="xl"
      className="testimonial-video-modal"
      contentClassName="testimonial-video-modal__content"
      aria-labelledby="testimonial-video-modal-title"
    >
      {show && video && (
        <>
          <Modal.Header closeButton className="testimonial-video-modal__header">
            <div>
              <Modal.Title id="testimonial-video-modal-title">{video.name}</Modal.Title>
              <p className="testimonial-video-modal__role">{video.role}</p>
            </div>
          </Modal.Header>
          <Modal.Body className="testimonial-video-modal__body">
            <div className="testimonial-video-modal__player">
              {driveId ? (
                driveUseIframe ? (
                  <iframe
                    key={`drive-iframe-${driveId}`}
                    src={googleDrivePreviewUrl(driveId)}
                    title={`Video: ${video.name}`}
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                  />
                ) : (
                  <video
                    ref={videoRef}
                    key={`drive-video-${driveId}`}
                    src={googleDriveStreamUrl(driveId)}
                    controls
                    autoPlay
                    playsInline
                    onError={() => setDriveUseIframe(true)}
                  />
                )
              ) : fileSrc ? (
                <video
                  ref={videoRef}
                  key={fileSrc}
                  src={fileSrc}
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <p className="testimonial-video-modal__empty">Video unavailable.</p>
              )}
            </div>
          </Modal.Body>
        </>
      )}
    </Modal>
  );
};

export default TestimonialVideoModal;
